/**
 * Google Analytics 4 e-commerce events.
 *
 * The site loaded gtag for years but never sent a single shopping event, so
 * GA4 reported zero revenue and Google Ads had nothing to optimise against.
 * This module is the one place those events are built, so the shape stays
 * consistent across the listing page, cart, checkout and payment page.
 *
 * Two things must not drift:
 *
 *  - `item_id` is the listing UUID, which is also the `id` column of the
 *    Google Merchant feed (see server/api-util/merchantFeed/feedRow.js). If
 *    they stop matching, Google can no longer join a sale to the product that
 *    was advertised, and shopping campaigns lose their revenue signal.
 *  - `transaction_id` is our own order number ("WO-20041"). The server sends a
 *    backup purchase event with the same id when payment is confirmed, and GA4
 *    keeps only one of the two. A different id on either side means every
 *    order is counted twice.
 *
 * Everything routes through gtagSafe, so nothing is sent from the server,
 * from a non-production host, or before the visitor has consented.
 */

import { gtagSafe, debugParams, hasMarketingConsent, isTrackingEnabled } from './tracking';
import { toMajorUnits } from './metaPixel';

export { toMajorUnits };

const CURRENCY_FALLBACK = 'SEK';

// A purchase must be counted once per order, even if the buyer reloads the
// confirmation page or returns to it from their inbox. sessionStorage survives
// a reload; a fresh session that revisits a long-finished order is not a
// realistic double-count, and the server backstop is de-duplicated by GA4 on
// transaction_id anyway.
const PURCHASE_REPORTED_KEY = 'wp_ga4_purchase_reported';

/**
 * One GA4 `items[]` entry.
 *
 * @param {Object} options
 * @param {String} options.listingId listing UUID — must equal the feed id
 * @param {String} [options.title]
 * @param {String} [options.brand] tyre / rim brand, never the car make
 * @param {String} [options.category]
 * @param {Number} [options.price] unit price in kronor (major units)
 * @param {Number} [options.quantity]
 * @return {Object|null} null when there is no listing id to report
 */
export const buildItem = ({ listingId, title, brand, category, price, quantity } = {}) => {
  if (!listingId) {
    return null;
  }
  return {
    item_id: listingId,
    ...(title ? { item_name: title } : {}),
    ...(brand ? { item_brand: brand } : {}),
    ...(category ? { item_category: category } : {}),
    ...(typeof price === 'number' && !Number.isNaN(price) ? { price } : {}),
    quantity: Number.parseInt(quantity, 10) > 0 ? Number.parseInt(quantity, 10) : 1,
  };
};

/**
 * Build an item straight from a denormalised listing entity.
 *
 * @param {Object} listing
 * @param {Number} [quantity]
 * @return {Object|null}
 */
export const buildItemFromListing = (listing, quantity = 1) => {
  const publicData = listing?.attributes?.publicData || {};
  const price = listing?.attributes?.price;
  return buildItem({
    listingId: listing?.id?.uuid,
    title: listing?.attributes?.title,
    brand: publicData.brand,
    category: publicData.categoryLevel1,
    price: toMajorUnits(price?.amount),
    quantity,
  });
};

/**
 * Build items from the `cartItems` the server stores on a transaction's
 * protectedData (see server/api-util/cartBundle.js — {listingId, title,
 * quantity, unitPrice, currency}). The same shape is used by the localStorage
 * cart's stored lines, which carry `price: {amount, currency}` instead.
 *
 * @param {Array<Object>} cartItems
 * @return {Array<Object>}
 */
export const buildItemsFromCartItems = (cartItems = []) =>
  (Array.isArray(cartItems) ? cartItems : [])
    .map(ci =>
      buildItem({
        listingId: ci?.listingId?.uuid || ci?.listingId,
        title: ci?.title,
        price: toMajorUnits(ci?.unitPrice ?? ci?.price?.amount),
        quantity: ci?.quantity ?? ci?.orderData?.quantity,
      })
    )
    .filter(Boolean);

// Line-item codes that are not the primary listing: add-ons, fees, tax,
// commissions and the bundled cart lines (reported from cartItems instead).
const NON_PRIMARY_LINE_ITEM_CODES = [
  'line-item/shipping-fee',
  'line-item/pickup-fee',
  'line-item/vat',
  'line-item/service',
  'line-item/miljoavgift',
  'line-item/cart-item',
  'line-item/provider-commission',
  'line-item/customer-commission',
];

/**
 * How many of the primary listing an order is for. The quantity is not stored
 * on the transaction's protectedData — it lives on the primary line item,
 * whose code depends on the listing's unit type — so it is found by
 * elimination.
 *
 * @param {Array<Object>} lineItems transaction.attributes.lineItems
 * @return {Number} defaults to 1 when it cannot be determined
 */
export const primaryQuantityFromLineItems = (lineItems = []) => {
  const primary = (Array.isArray(lineItems) ? lineItems : []).find(
    li => li?.code && !NON_PRIMARY_LINE_ITEM_CODES.includes(li.code)
  );
  // SDK quantities are Decimal instances, so go via their string form.
  const quantity = Number.parseInt(String(primary?.quantity ?? ''), 10);
  return quantity > 0 ? quantity : 1;
};

/** Shared payload for every e-commerce event. */
const ecommerceParams = (items, value, currency, extra = {}) => ({
  currency: currency || CURRENCY_FALLBACK,
  ...(typeof value === 'number' && !Number.isNaN(value) ? { value } : {}),
  items: (items || []).filter(Boolean),
  ...extra,
  ...debugParams(),
});

/**
 * Send one e-commerce event. No-ops (returning false) when there is nothing to
 * report or tracking is not allowed.
 *
 * @param {String} eventName GA4 event name, e.g. 'view_item'
 * @param {Object} params
 * @return {Boolean} true when the event was sent
 */
export const trackEcommerceEvent = (eventName, params) => {
  if (!eventName || !params?.items?.length) {
    return false;
  }
  return gtagSafe('event', eventName, params);
};

/** A shopper opened a listing page. */
export const trackViewItem = ({ items, value, currency } = {}) =>
  trackEcommerceEvent('view_item', ecommerceParams(items, value, currency));

/** A shopper put a listing in the cart. */
export const trackAddToCart = ({ items, value, currency } = {}) =>
  trackEcommerceEvent('add_to_cart', ecommerceParams(items, value, currency));

/** A shopper took a listing out of the cart. */
export const trackRemoveFromCart = ({ items, value, currency } = {}) =>
  trackEcommerceEvent('remove_from_cart', ecommerceParams(items, value, currency));

/** A shopper reached the checkout page with a priced order. */
export const trackBeginCheckout = ({ items, value, currency } = {}) =>
  trackEcommerceEvent('begin_checkout', ecommerceParams(items, value, currency));

/**
 * A delivery method was chosen and submitted.
 * @param {String} [options.shippingTier] 'shipping' | 'pickup'
 */
export const trackAddShippingInfo = ({ items, value, currency, shippingTier } = {}) =>
  trackEcommerceEvent(
    'add_shipping_info',
    ecommerceParams(items, value, currency, shippingTier ? { shipping_tier: shippingTier } : {})
  );

/**
 * The buyer submitted their payment details.
 * @param {String} [options.paymentType] e.g. 'card', 'klarna', 'off_platform'
 */
export const trackAddPaymentInfo = ({ items, value, currency, paymentType } = {}) =>
  trackEcommerceEvent(
    'add_payment_info',
    ecommerceParams(items, value, currency, paymentType ? { payment_type: paymentType } : {})
  );

/**
 * Enhanced conversions (BR-6). Passes the buyer's email to Google, which
 * hashes it client-side, so an ad click can be matched to the sale. Marketing
 * consent only — analytics consent is not enough to send personal data.
 *
 * @param {String} email
 * @return {Boolean} true when the value was set
 */
export const setEnhancedConversionData = email => {
  if (!email || !hasMarketingConsent()) {
    return false;
  }
  return gtagSafe('set', 'user_data', { email: String(email).trim().toLowerCase() });
};

/**
 * Payment succeeded.
 *
 * @param {Object} options
 * @param {String} options.transactionId our order number, e.g. 'WO-20041' —
 *   the same value the server backstop uses, which is what GA4 de-duplicates on
 * @param {Array<Object>} options.items
 * @param {Number} [options.value] order total in kronor, add-on services included
 * @param {String} [options.currency]
 * @param {String} [options.email] buyer email, for enhanced conversions
 * @return {Boolean} true when the event was sent
 */
export const trackPurchase = ({ transactionId, items, value, currency, email } = {}) => {
  if (!transactionId || !items?.length) {
    return false;
  }
  if (hasPurchaseBeenReported(transactionId)) {
    return false;
  }

  setEnhancedConversionData(email);

  const sent = trackEcommerceEvent(
    'purchase',
    ecommerceParams(items, value, currency, { transaction_id: transactionId })
  );
  if (sent) {
    markPurchaseReported(transactionId);
  }
  return sent;
};

/**
 * A seller published a listing. Not a shopping event, but it is the supply-side
 * conversion the business steers seller acquisition campaigns on.
 *
 * @param {Object} listing published listing entity
 * @return {Boolean}
 */
export const trackListingPublished = listing => {
  const item = buildItemFromListing(listing);
  if (!item) {
    return false;
  }
  const price = listing?.attributes?.price;
  return trackEcommerceEvent(
    'listing_published',
    ecommerceParams([item], toMajorUnits(price?.amount), price?.currency)
  );
};

// ---------------------------------------------------------------------------
// Purchase de-duplication
// ---------------------------------------------------------------------------

const readReportedPurchases = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return [];
  }
  try {
    const raw = window.sessionStorage.getItem(PURCHASE_REPORTED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

/** True when this order number has already produced a purchase event. */
export const hasPurchaseBeenReported = transactionId =>
  !!transactionId && readReportedPurchases().includes(transactionId);

/** Remember that this order number has been reported. */
export const markPurchaseReported = transactionId => {
  if (!transactionId || typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    const next = readReportedPurchases().concat(transactionId).slice(-20);
    window.sessionStorage.setItem(PURCHASE_REPORTED_KEY, JSON.stringify(next));
  } catch (e) {
    // Quota / privacy mode — the in-page ref guard still covers the common case.
  }
};

/** Re-exported so callers can skip building a payload they cannot send. */
export { isTrackingEnabled };

/**
 * Listing-page interaction events (P25009-482 BR-7), so the new layout can be
 * measured against the old one: how often a buyer checks the fit, and how
 * often they contact the seller instead of buying.
 *
 *   fit_check       a fit result appeared after the buyer entered or picked a car
 *                   (result: fits | no_fit | uncertain)
 *   message_seller  "Skicka meddelande" link or the mobile chat icon
 *   contact_phone   a phone number was revealed or tapped
 *   contact_email   an e-mail address was revealed or tapped
 *
 * Same consent gate as every other event (gtagSafe).
 *
 * @param {String} eventName one of the names above
 * @param {Object} [params] e.g. { listing_id, result, surface }
 * @return {Boolean} true when the event was sent
 */
export const trackListingEvent = (eventName, params = {}) => {
  if (!eventName) return false;
  return gtagSafe('event', eventName, { ...params, ...debugParams() });
};
